import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";

import CalendarSelector from "./CalendarSelector";
import ClientSelector from "./ClientSelector";
import ConfirmationStatus from "./ConfirmationStatus";
import PrefixSelector from "./PrefixSelector";
import DatesContainer from "./DatesContainer";
import DueDatesContainer from "./DueDatesContainer";
import JobNumber from "./JobNumber";
import JobTypeContainer from "./JobTypeContainer";
import CommentsImagesScheduledContainer from "./CommentsImagesScheduledContainer";
import CameraSettings from "./CameraSettings";
import StaffContainer from "./StaffContainer";
import EventNotes from "./EventNotes";
import RecurringEventsContainer from "./RecurringEventsContainer";
import BriefsContainer from "./BriefsContainer";
import NotesContainer from "../NotesContainer";
import InvoicingNotesContainer from "../InvoicingNotesContainer";
import ModelsContainer from "../ModelsContainer";
import HMUContainer from "../HMUContainer";
import TableRowsTwoToneIcon from "@mui/icons-material/TableRowsTwoTone";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AttachmentsV2 from "../AttachmentsV2";
import URLModalV2 from "../JobTab/URLModalV2";
import ChecklistsV2 from "../JobTab/ChecklistsV2";
import SuppliedFoldersDialog from "../JobTab/SuppliedFoldersDialog";
import SettingsIcon from "@mui/icons-material/Settings";
import { useAtom } from "jotai";
import { themeAtom } from "../../../../-Atoms";

const PreProdTab = ({
  jobData,
  jobClientData,
  isEventTomorrow,
  setHasUnsavedChanges,
  confirmationStatusEmailOpen,
  setConfirmationStatusEmailOpen,
  firstConfirmationStatusEmailOpen,
  setFirstConfirmationStatusEmailOpen,
  setConfirmationEmailData,
  isSpecialEvent,
  allMultipleDayJobs = [],
}) => {
  const [theme] = useAtom(themeAtom);

  const [urlModalUrl, setUrlModalUrl] = useState("");
  const [URLModalOpen, setURLModalOpen] = useState(false);
  const [visibleFoldersDialogOpen, setVisibleFoldersDialogOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const tabRef = useRef(null);
  const attachmentsRef = useRef(null);
  const supplyPortalUrl = jobData?.id
    ? process.env.NODE_ENV === "production"
      ? `https://supply.imagesthatsell.com.au/${jobData.id}`
      : `https://localhost:3000/supply/${jobData.id}`
    : null;

  // Drag and drop file upload handler
  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter(prev => prev + 1);
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragOver(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter(prev => prev - 1);
      if (dragCounter <= 1) {
        setIsDragOver(false);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDragCounter(0);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0 && attachmentsRef.current) {
        attachmentsRef.current.handleGlobalFileUpload(files);
      }
    };

    const tabElement = tabRef.current;
    if (tabElement) {
      tabElement.addEventListener("dragenter", handleDragEnter);
      tabElement.addEventListener("dragleave", handleDragLeave);
      tabElement.addEventListener("dragover", handleDragOver);
      tabElement.addEventListener("drop", handleDrop);
    }

    return () => {
      if (tabElement) {
        tabElement.removeEventListener("dragenter", handleDragEnter);
        tabElement.removeEventListener("dragleave", handleDragLeave);
        tabElement.removeEventListener("dragover", handleDragOver);
        tabElement.removeEventListener("drop", handleDrop);
      }
    };
  }, [dragCounter]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        ref={tabRef}
        sx={{
          p: 1,
          position: "relative",
        }}
      >
        <AnimatePresence>
          {isDragOver && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                background: theme === "dark"
                  ? "rgba(100, 100, 255, 0.1)"
                  : "rgba(100, 100, 255, 0.1)",
                border: `3px dashed ${theme === "dark" ? "rgba(100, 100, 255, 0.8)" : "rgba(100, 100, 255, 0.8)"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(4px)",
                pointerEvents: "none",
              }}
            >
              <Box sx={{
                textAlign: "center",
                color: theme === "dark" ? "rgba(100, 100, 255, 0.9)" : "rgba(100, 100, 255, 0.9)",
                background: theme === "dark" ? "rgba(0, 0, 0, 0.8)" : "rgba(255, 255, 255, 0.9)",
                borderRadius: 3,
                p: 4,
                boxShadow: theme === "dark"
                  ? "0 8px 32px rgba(0, 0, 0, 0.5)"
                  : "0 8px 32px rgba(0, 0, 0, 0.2)",
              }}>
                <CloudUploadIcon sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                  Drop files to upload
                </Typography>
                <Typography variant="body1">
                  Files will be added to attachments
                </Typography>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {!isSpecialEvent && (
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <Button
              variant="contained"
              startIcon={<LocalShippingIcon />}
              fullWidth
              component="a"
              href={supplyPortalUrl ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              disabled={!supplyPortalUrl}
              sx={{
                backgroundColor:
                  theme === "dark"
                    ? "rgba(150,100,255,0.8)"  // Purple in dark theme
                    : "rgba(150,100,255,0.9)", // Purple in light theme
                color: "#ffffff",
                padding: "8px 16px",
                borderRadius: "8px",
                textTransform: "none",
                fontSize: 15,
                fontWeight: 600,
                boxShadow:
                  theme === "dark"
                    ? "0 2px 8px rgba(0,0,0,0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(150,100,255,0.9)"
                      : "rgba(150,100,255,1)",
                  boxShadow:
                    theme === "dark"
                      ? "0 4px 12px rgba(0,0,0,0.4)"
                      : "0 4px 12px rgba(0,0,0,0.2)",
                },
                height: 45,
              }}
            >
              Open Supply Portal
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!supplyPortalUrl}
              sx={{
                minWidth: 40,
                backgroundColor:
                  theme === "dark"
                    ? "rgba(150,100,255,0.8)"  // Purple to match main button in dark theme
                    : "rgba(150,100,255,0.9)", // Purple to match main button in light theme
                color: "#ffffff",
                "&:hover": {
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(150,100,255,0.9)"
                      : "rgba(150,100,255,1)",
                },
                height: 45,
              }}
              onClick={() => {
                if (!supplyPortalUrl) return;
                navigator.clipboard.writeText(supplyPortalUrl).then(() => {
                  setSnackbarMessage("Supply portal link copied to clipboard");
                  setSnackbarSeverity("success");
                  setSnackbarOpen(true);
                });
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setVisibleFoldersDialogOpen(true)}
              sx={{
                minWidth: 40,
                backgroundColor:
                  theme === "dark"
                    ? "rgba(150,100,255,0.8)"
                    : "rgba(150,100,255,0.9)",
                color: "#ffffff",
                "&:hover": {
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(150,100,255,0.9)"
                      : "rgba(150,100,255,1)",
                },
                height: 45,
              }}
              title="Change Supply Folder Visibility"
            >
              <SettingsIcon />
            </Button>
          </Box>
        )}

        {!isSpecialEvent && (
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <Button
              variant="contained"
              startIcon={<TableRowsTwoToneIcon />}
              fullWidth
              component="a"
              href={jobClientData?.spreadsheetURL}
              disabled={!jobClientData?.spreadsheetURL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                backgroundColor:
                  theme === "dark"
                    ? "rgba(83,162,108,0.9)"  // Google Sheets green in dark theme
                    : "rgba(83,162,108,1.0)", // Google Sheets green in light theme
                color: "#ffffff",
                padding: "8px 16px",
                borderRadius: "8px",
                textTransform: "none",
                fontSize: 15,
                fontWeight: 600,
                boxShadow:
                  theme === "dark"
                    ? "0 2px 8px rgba(0,0,0,0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(83,162,108,1.0)"
                      : "rgba(63,142,88,1.0)",
                  boxShadow:
                    theme === "dark"
                      ? "0 4px 12px rgba(0,0,0,0.4)"
                      : "0 4px 12px rgba(0,0,0,0.2)",
                },
                height: 45,
              }}
            >
              Open Shootlist
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!jobClientData?.spreadsheetURL}
              sx={{
                minWidth: 40,
                backgroundColor:
                  theme === "dark"
                    ? "rgba(83,162,108,0.9)"  // Google Sheets green to match main button in dark theme
                    : "rgba(83,162,108,1.0)", // Google Sheets green to match main button in light theme
                color: "#ffffff",
                "&:hover": {
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(83,162,108,1.0)"
                      : "rgba(63,142,88,1.0)",
                },
                height: 45,
              }}
              onClick={() => {
                navigator.clipboard.writeText(jobClientData.spreadsheetURL).then(() => {
                  setSnackbarMessage("Shootlist copied to clipboard");
                  setSnackbarSeverity("success");
                  setSnackbarOpen(true);
                });
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </Button>
          </Box>
        )}

        <CalendarSelector jobData={jobData} isEventTomorrow={isEventTomorrow} />
        {!isSpecialEvent && (
          <>
            <ClientSelector jobData={jobData} />
            <ConfirmationStatus
              jobData={jobData}
              jobClientData={jobClientData}
              confirmationStatusEmailOpen={confirmationStatusEmailOpen}
              setConfirmationStatusEmailOpen={setConfirmationStatusEmailOpen}
              firstConfirmationStatusEmailOpen={
                firstConfirmationStatusEmailOpen
              }
              setFirstConfirmationStatusEmailOpen={
                setFirstConfirmationStatusEmailOpen
              }
              setConfirmationEmailData={setConfirmationEmailData}
            />
          </>
        )}
        <PrefixSelector
          jobData={jobData}
          setHasUnsavedChanges={setHasUnsavedChanges}
        />
        <DatesContainer jobData={jobData} isSpecialEvent={isSpecialEvent} />
        {!isSpecialEvent && (
          <>
            <DueDatesContainer jobData={jobData} />
            <JobTypeContainer
              jobData={jobData}
              clientData={jobClientData}
              setHasUnsavedChanges={setHasUnsavedChanges}
              allMultipleDayJobs={allMultipleDayJobs}
            />
            <JobNumber
              jobData={jobData}
              allMultipleDayJobs={allMultipleDayJobs}
              setHasUnsavedChanges={setHasUnsavedChanges}
            />

            <CommentsImagesScheduledContainer
              jobData={jobData}
              setHasUnsavedChanges={setHasUnsavedChanges}
            />
            <CameraSettings
              jobData={jobData}
              clientData={jobClientData}
              setHasUnsavedChanges={setHasUnsavedChanges}
            />
          </>
        )}
        <StaffContainer
          jobData={jobData}
          isEventTomorrow={isEventTomorrow}
          setHasUnsavedChanges={setHasUnsavedChanges}
          isSpecialEvent={isSpecialEvent}
        />
        <EventNotes
          jobData={jobData}
          setHasUnsavedChanges={setHasUnsavedChanges}
        />
        {isSpecialEvent && <RecurringEventsContainer jobData={jobData} />}
        {!isSpecialEvent && (
          <>
            <AttachmentsV2
              ref={attachmentsRef}
              jobData={jobData}
              clientData={jobClientData}
              setUrlModalUrl={setUrlModalUrl}
              setURLModalOpen={setURLModalOpen}
            />
            <BriefsContainer
              jobData={jobData}
              clientData={jobClientData}
              setHasUnsavedChanges={setHasUnsavedChanges}
            />
            <NotesContainer
              jobData={jobData}
              clientData={jobClientData}
              setHasUnsavedChanges={setHasUnsavedChanges}
            />
            <InvoicingNotesContainer
              jobData={jobData}
              clientData={jobClientData}
              setHasUnsavedChanges={setHasUnsavedChanges}
            />
            <ModelsContainer
              jobData={jobData}
              setHasUnsavedChanges={setHasUnsavedChanges}
            />
            <HMUContainer
              jobData={jobData}
              setHasUnsavedChanges={setHasUnsavedChanges}
            />
          </>
        )}

        {jobData.studio === "Post Production" && (
          <Box sx={{ mt: 2 }}>
            <ChecklistsV2
              jobData={jobData}
              clientData={jobClientData}
              setUrlModalUrl={setUrlModalUrl}
              setURLModalOpen={setURLModalOpen}
              idList={null}
            />
          </Box>
        )}

        <URLModalV2
          open={URLModalOpen}
          handleClose={() => setURLModalOpen(false)}
          url={urlModalUrl}
        />

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <SuppliedFoldersDialog
          open={visibleFoldersDialogOpen}
          onClose={() => setVisibleFoldersDialogOpen(false)}
          jobData={jobData}
          setHasUnsavedChanges={setHasUnsavedChanges}
          allMultipleDayJobs={allMultipleDayJobs}
        />
      </Box >
    </motion.div >
  );
};

export default PreProdTab;
